import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { getStringWithoutLeadingSlash } from 'helpers/parsing/stringWithoutSlash';
import Head from 'next/head';
import { StoreDetailType } from 'types/store';

type StoreDetailMetadataProps = {
    store: StoreDetailType;
};

const schemaDayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const StoreDetailMetadata: FC<StoreDetailMetadataProps> = ({ store }) => {
    const { url: domainUrl } = useDomainConfig();
    const storeUrl = domainUrl + getStringWithoutLeadingSlash(store.slug);
    const openingHoursSpecification =
        store.openingHours?.days.flatMap(({ alwaysClosed, dayOfTheWeek, intervals }) =>
            alwaysClosed
                ? []
                : intervals.map(({ opening, closing }) => ({
                      '@type': 'OpeningHoursSpecification',
                      dayOfWeek: schemaDayNames[dayOfTheWeek],
                      opens: opening,
                      closes: closing,
                  })),
        ) ?? [];
    const metadata = {
        '@context': 'https://schema.org',
        '@type': 'Store',
        name: store.storeName,
        '@id': storeUrl,
        url: storeUrl,
        telephone: store.telephone ?? undefined,
        address: {
            '@type': 'PostalAddress',
            streetAddress: store.street ?? undefined,
            addressLocality: store.city ?? undefined,
            postalCode: store.postcode ?? undefined,
            addressCountry: store.country?.code ?? undefined,
        },
        geo:
            store.locationLatitude !== null && store.locationLongitude !== null
                ? {
                      '@type': 'GeoCoordinates',
                      latitude: store.locationLatitude,
                      longitude: store.locationLongitude,
                  }
                : undefined,
        openingHoursSpecification: openingHoursSpecification.length > 0 ? openingHoursSpecification : undefined,
    };

    return (
        <Head>
            <script
                key="store-detail-metadata"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(metadata).replace(/</g, '\\u003c') }}
                id="store-detail-metadata"
                type="application/ld+json"
            />
        </Head>
    );
};
